import React from "react";
import CardClass from "../../CardClass";
import MUILoadingButton from "@mui/lab/LoadingButton";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import ReportSettingDateRange from "../../ReportSetting/ReportSettingDateRange";
import ReportSettingDate from "../../ReportSetting/ReportSettingDate";
import ReportSettingUserByLocation from "../../ReportSetting/ReportSettingUserByLocation";
import ReportSettingDraggableList from "../../ReportSetting/ReportSettingDraggableList";
import ReportSettingOrderStatusReasonMapping from "../../ReportSetting/ReportSettingOrderStatusReasonMapping";
import ReportSettingMarketingCampaignFilter from "../../ReportSetting/ReportSettingMarketingCampaignFilter";
import ReportSettingRegionLocation from "../../ReportSetting/ReportSettingRegionLocation";
import ReportSettingAccountStatus from "../../ReportSetting/ReportSettingAccountStatus";
import ReportSettingUser from "../../ReportSetting/ReportSettingUser";
import ReportSettingOnlyActiveAccounts from "../../ReportSetting/ReportSettingOnlyActiveAccounts";
import Setting from "../../../Utility/Setting";
import Menu from "../../Menu";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIStack from "@mui/material/Stack";
import MUIBox from "@mui/material/Box";
import ReportSettingOrderActivityType from "../../ReportSetting/ReportSettingOrderActivityType";
import Dashboard from "../../../Utility/Crud/Dashboard";

export default class CardReportSettings extends CardClass {
  constructor(props) {
    super(props);

    this.state = {
      settings: Setting.get(this.generateSettingKey()) || {},
      validationIssues: {},
      showValidationIssues: false,
      collapsed: this.props.initiallyCollapsed || false,
      hasRun: this.props.initiallyCollapsed || false,
    };
  }

  generateSettingKey() {
    return (
      window.location.pathname.replace(/^\/|\/$/g, "").replace(/\//g, ".") +
      ".settings"
    );
  }

  saveSettings() {
    Setting.set(this.generateSettingKey(), this.state.settings);
  }

  componentDidUpdate(prevProps) {
    if (Dashboard.isDashboardRoute()) {
      if (prevProps.settings !== this.props.settings) {
        this.setState({
          settings: this.props.settings,
        });
      }
    }
  }

  /**
   * Render the content.
   */
  renderContent() {
    const handleSettingsChange = (key, settings, validationIssues) => {
      // Any time a setting component is created or the data changes, it
      // sends back the entire updated settings and validation objects.
      this.setState((prevState) => ({
        settings: {
          ...prevState.settings,
          [key]: settings,
        },
        validationIssues: {
          ...prevState.validationIssues,
          [key]: validationIssues,
        },
      }));
    };

    const settingComponents = {
      dateRange: ReportSettingDateRange,
      date: ReportSettingDate,
      userByLocation: ReportSettingUserByLocation,
      draggableList: ReportSettingDraggableList,
      orderStatusReasons: ReportSettingOrderStatusReasonMapping,
      marketingCampaignFilter: ReportSettingMarketingCampaignFilter,
      accountStatus: ReportSettingAccountStatus,
      user: ReportSettingUser,
      regionLocation: ReportSettingRegionLocation,
      onlyActiveAccounts: ReportSettingOnlyActiveAccounts,
      orderActivityTypes: ReportSettingOrderActivityType,
      // Add more here...
    };

    const settingComponentsElements = Object.keys(
      this.props.settingComponents,
    ).map((key) => {
      const SettingComponent = settingComponents[key];
      if (SettingComponent) {
        return (
          <SettingComponent
            key={key}
            settings={this.state.settings[key]}
            grouping={
              this.props.settingComponents[key].grouping
                ? this.props.settingComponents[key].grouping
                : null
            }
            allGroups={
              this.props.settingComponents[key].allGroups
                ? this.props.settingComponents[key].allGroups
                : null
            }
            validationRules={this.props.settingComponents[key].validationRules}
            validationIssues={this.state.validationIssues[key]}
            showValidationIssues={this.state.showValidationIssues}
            onSettingsChange={(settings, validationIssues) => {
              handleSettingsChange(key, settings, validationIssues);
            }}
            presetList={this.props.settingComponents[key].presetList}
            presetOnly={this.props.settingComponents[key].presetOnly}
            userRolesToFilter={
              this.props.settingComponents[key].userRolesToFilter
            }
            restrictToCurrentUser={
              this.props.settingComponents[key].restrictToCurrentUser
            }
            restrictToCurrentUserLocations={
              this.props.settingComponents[key].restrictToCurrentUserLocations
            }
            dateViews={this.props.settingComponents[key].dateViews}
            regionId={this.props.settingComponents[key].regionId}
            hint={this.props.settingComponents[key].hint}
            showInactiveUsers={
              this.props.settingComponents[key].showInactiveUsers
            }
            readOnly={this.state.collapsed}
          />
        );
      }
      return null;
    });

    // When collapsed, use grid layout for better wrapping
    if (this.state.collapsed) {
      return (
        <MUIBox
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 1,
          }}
        >
          {settingComponentsElements}
        </MUIBox>
      );
    }

    // When expanded, use vertical stack
    return (
      <MUIStack direction="column" spacing={1}>
        {settingComponentsElements}
      </MUIStack>
    );
  }

  /**
   * @returns {string} The title of the card.
   */
  getTitle() {
    if (this.props.hideTitle === true) {
      return null;
    }

    return "Settings";
  }

  /**
   * Renders add member button.
   */
  getActions() {
    const handleRun = () => {
      this.setState({ showValidationIssues: true });
      if (
        Object.values(this.state.validationIssues).some(
          (object) => Object.keys(object).length > 0,
        )
      ) {
        NotistackEnqueueSnackbar("Please correct the displayed issues.", {
          variant: "error",
        });
      } else {
        this.saveSettings();
        this.props.onRun(this.state.settings);
        // Auto-collapse and mark as run
        this.setState({ collapsed: true, hasRun: true });
      }
    };

    if (Dashboard.isDashboardRoute()) {
      return null;
    }

    // When collapsed: only show Edit button (far right) if report has been run
    if (this.state.collapsed) {
      return [
        this.state.hasRun && (
          <MUILoadingButton
            key="edit"
            variant="outlined"
            onClick={() => this.setState({ collapsed: false })}
            loading={this.props.isLoading}
            disabled={this.props.isLoading}
          >
            Edit
          </MUILoadingButton>
        ),
      ].filter(Boolean);
    }

    // When expanded: show Run button and Menu (no Edit button)
    return [
      <MUILoadingButton
        key="run"
        loading={this.props.isLoading}
        component="label"
        variant="contained"
        onClick={handleRun}
      >
        Run
      </MUILoadingButton>,
      this.props.menu ? (
        <Menu
          key="menu"
          menuItems={this.props.menuActions
            .map((menuAction, index) => (
              <MUIMenuItem
                key={index}
                onClick={() => {
                  menuAction.onClick();
                }}
              >
                {menuAction.icon ? (
                  <MUIListItemIcon>{menuAction.icon}</MUIListItemIcon>
                ) : null}
                {menuAction.label}
              </MUIMenuItem>
            ))
            .filter(Boolean)}
        ></Menu>
      ) : (
        <></>
      ),
    ].filter(Boolean);
  }
}
